import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, LogOut, Package, Pencil, Plus, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Wordmark } from "@/components/Navbar";

const EMPTY_PRODUCT = { name: "", category: "spices", tagline: "", description: "", image: "", moq: "", packaging: "", available: true, specsText: "" };

const inputCls =
  "w-full rounded-xl border border-stone2 bg-white px-4 py-2.5 text-sm text-ink placeholder:text-pebble/70 outline-none transition-colors focus:border-gold focus:ring-2 focus:ring-gold/20";

const STATUS_STYLES = {
  new: "bg-chilli/10 text-chilli",
  contacted: "bg-gold/15 text-gold",
  quoted: "bg-botanical/10 text-botanical",
  closed: "bg-pebble/15 text-pebble",
};

function ProductForm({ initial, onSave, onCancel, saving }) {
  const [form, setForm] = useState(initial);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    const specs = form.specsText
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean)
      .map((l) => {
        const idx = l.indexOf(":");
        return idx === -1 ? { key: l, value: "" } : { key: l.slice(0, idx).trim(), value: l.slice(idx + 1).trim() };
      });
    const { specsText, ...rest } = form;
    onSave({ ...rest, specs });
  };

  return (
    <form data-testid="admin-product-form" onSubmit={submit} className="mt-6 rounded-2xl border border-stone2 bg-white p-6">
      <div className="grid gap-4 sm:grid-cols-2">
        <input data-testid="product-form-name-input" required placeholder="Product Name *" value={form.name} onChange={set("name")} className={inputCls} />
        <select data-testid="product-form-category-select" value={form.category} onChange={set("category")} className={inputCls}>
          <option value="spices">Spices</option>
          <option value="oils">Cold Pressed Oils</option>
        </select>
        <input placeholder="Tagline" value={form.tagline} onChange={set("tagline")} className={`${inputCls} sm:col-span-2`} />
        <textarea placeholder="Description" rows={3} value={form.description} onChange={set("description")} className={`${inputCls} resize-none sm:col-span-2`} />
        <input placeholder="Image URL" value={form.image} onChange={set("image")} className={`${inputCls} sm:col-span-2`} />
        <input placeholder="MOQ (e.g. 1 Metric Ton)" value={form.moq} onChange={set("moq")} className={inputCls} />
        <input placeholder="Packaging (e.g. 25 kg PP bags)" value={form.packaging} onChange={set("packaging")} className={inputCls} />
        <textarea
          placeholder={"Specs, one per line as Key: Value\nCurcumin: ≥ 5%\nMoisture: ≤ 10%"}
          rows={4}
          value={form.specsText}
          onChange={set("specsText")}
          className={`${inputCls} resize-none font-mono text-xs sm:col-span-2`}
        />
        <label className="flex items-center gap-2 text-sm text-smoke">
          <input data-testid="product-form-available-checkbox" type="checkbox" checked={form.available} onChange={set("available")} className="h-4 w-4 accent-gold" />
          Available for export
        </label>
      </div>
      <div className="mt-5 flex gap-3">
        <button data-testid="product-form-save-button" type="submit" disabled={saving} className="rounded-full bg-botanical px-6 py-2.5 text-sm font-semibold text-cream transition-transform hover:scale-[1.03] disabled:opacity-60">
          {saving ? "Saving..." : "Save Product"}
        </button>
        <button type="button" onClick={onCancel} className="rounded-full border border-stone2 px-6 py-2.5 text-sm font-medium text-smoke hover:border-gold">
          Cancel
        </button>
      </div>
    </form>
  );
}

function ProductsPanel() {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const toForm = (p) => ({ ...p, specsText: (p.specs || []).map((s) => `${s.key}: ${s.value}`).join("\n") });

  const save = async (payload) => {
    setSaving(true);
    try {
      if (adding) await api.post("/admin/products", payload);
      else await api.put(`/admin/products/${editing.id}`, payload);
      toast.success("Product saved");
      setAdding(false);
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try {
      await api.delete(`/admin/products/${p.id}`);
      toast.success("Product deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-products-panel">
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-xl font-semibold text-ink">Products ({products.length})</h2>
        {!adding && !editing && (
          <button data-testid="admin-add-product-button" onClick={() => setAdding(true)} className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-semibold text-night transition-transform hover:scale-[1.03]">
            <Plus size={15} /> Add Product
          </button>
        )}
      </div>

      {adding && <ProductForm initial={EMPTY_PRODUCT} onSave={save} onCancel={() => setAdding(false)} saving={saving} />}
      {editing && <ProductForm initial={toForm(editing)} onSave={save} onCancel={() => setEditing(null)} saving={saving} />}

      <div className="mt-6 space-y-3">
        {products.map((p) => (
          <div key={p.id} data-testid={`admin-product-row-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="flex items-center gap-4 rounded-2xl border border-stone2 bg-white p-4">
            <img src={p.image} alt="" className="h-14 w-14 shrink-0 rounded-xl object-cover" />
            <div className="min-w-0 flex-1">
              <div className="truncate font-serif text-base font-semibold text-ink">{p.name}</div>
              <div className="text-xs text-pebble">
                {p.category === "spices" ? "Spice" : "Cold Pressed Oil"} · MOQ {p.moq} {p.available ? "" : "· On request"}
              </div>
            </div>
            <button data-testid={`admin-edit-product-${p.id}`} onClick={() => { setEditing(p); setAdding(false); }} className="flex h-9 w-9 items-center justify-center rounded-full border border-stone2 text-smoke transition-colors hover:border-gold hover:text-gold" aria-label="Edit">
              <Pencil size={14} />
            </button>
            <button data-testid={`admin-delete-product-${p.id}`} onClick={() => remove(p)} className="flex h-9 w-9 items-center justify-center rounded-full border border-stone2 text-smoke transition-colors hover:border-chilli hover:text-chilli" aria-label="Delete">
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function EnquiriesPanel() {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/enquiries").then((r) => setEnquiries(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/enquiries/${id}`, { status });
      setEnquiries((list) => list.map((e) => (e.id === id ? { ...e, status } : e)));
      toast.success("Status updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-gold" /></div>;

  return (
    <div data-testid="admin-enquiries-panel">
      <h2 className="font-serif text-xl font-semibold text-ink">Enquiries ({enquiries.length})</h2>
      {enquiries.length === 0 ? (
        <p data-testid="admin-enquiries-empty" className="mt-6 rounded-2xl border border-dashed border-stone2 bg-white p-10 text-center text-sm text-pebble">
          No enquiries yet. New quote requests from the website will appear here.
        </p>
      ) : (
        <div className="mt-6 space-y-4">
          {enquiries.map((e) => (
            <div key={e.id} data-testid={`admin-enquiry-${e.id}`} className="rounded-2xl border border-stone2 bg-white p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="font-serif text-base font-semibold text-ink">{e.name} {e.company ? <span className="text-sm font-normal text-pebble">· {e.company}</span> : null}</div>
                  <div className="mt-1 text-xs text-pebble">{e.email} {e.phone ? `· ${e.phone}` : ""}</div>
                </div>
                <select
                  data-testid={`admin-enquiry-status-${e.id}`}
                  value={e.status}
                  onChange={(ev) => setStatus(e.id, ev.target.value)}
                  className={`rounded-full border-0 px-3 py-1.5 text-xs font-semibold capitalize outline-none ${STATUS_STYLES[e.status] || ""}`}
                >
                  {Object.keys(STATUS_STYLES).map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-sm text-smoke">
                <span className="inline-flex items-center gap-1.5"><Package size={14} className="text-gold" />{e.commodity || "—"}</span>
                {e.quantity && <span>Qty: {e.quantity}</span>}
                {e.destination && <span>To: {e.destination}</span>}
                {e.incoterm && <span>Incoterm: {e.incoterm}</span>}
              </div>
              {e.message && <p className="mt-3 rounded-xl bg-sand px-4 py-3 text-sm text-smoke">{e.message}</p>}
              <div className="mt-3 text-[11px] uppercase tracking-[0.14em] text-pebble">
                {e.created_at ? new Date(e.created_at).toLocaleString() : ""}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("tsugiva_token") || "");
  const [checking, setChecking] = useState(!!localStorage.getItem("tsugiva_token"));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("enquiries");

  useEffect(() => {
    if (!token) return;
    api.get("/auth/me")
      .then(() => setChecking(false))
      .catch(() => {
        localStorage.removeItem("tsugiva_token");
        setToken("");
        setChecking(false);
      });
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("tsugiva_token", data.token);
      setToken(data.token);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("tsugiva_token");
    setToken("");
  };

  if (checking) {
    return <div className="flex min-h-screen items-center justify-center bg-cream"><Loader2 className="animate-spin text-gold" size={32} /></div>;
  }

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-night px-4">
        <div className="grain-overlay" />
        <div className="w-full max-w-md">
          <div className="mb-8 text-center"><Wordmark dark /></div>
          <form data-testid="admin-login-form" onSubmit={login} className="rounded-3xl border border-cream/10 bg-white/[0.05] p-8 backdrop-blur-sm">
            <h1 className="font-serif text-2xl font-semibold text-cream">Admin Portal</h1>
            <p className="mt-1.5 text-sm text-cream/55">Sign in to manage products & enquiries</p>
            <div className="mt-7 space-y-4">
              <input data-testid="admin-username-input" type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
              <input data-testid="admin-password-input" type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} />
            </div>
            {error && <p data-testid="admin-login-error" className="mt-4 rounded-xl bg-chilli/15 px-4 py-2.5 text-sm text-red-300">{error}</p>}
            <button data-testid="admin-login-button" type="submit" disabled={loading} className="mt-6 w-full rounded-full bg-gold py-3.5 text-sm font-semibold text-night transition-transform hover:scale-[1.02] disabled:opacity-60">
              {loading ? "Signing in..." : "Sign In"}
            </button>
            <button
              type="button"
              data-testid="admin-demo-fill-button"
              onClick={() => { setEmail("admin@tsugiva.com"); setPassword("tsugiva2026"); }}
              className="mt-3 w-full rounded-full border border-cream/20 py-3 text-xs font-medium text-cream/60 transition-colors hover:border-amberwarm hover:text-amberwarm"
            >
              Fill demo credentials
            </button>
          </form>
          <Link to="/" data-testid="admin-back-home-link" className="mt-6 flex items-center justify-center gap-2 text-sm text-cream/50 transition-colors hover:text-amberwarm">
            <ArrowLeft size={14} /> Back to website
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      <header className="border-b border-stone2 bg-white/80 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 sm:px-8">
          <Wordmark />
          <div className="flex items-center gap-3">
            <Link to="/" data-testid="admin-view-site-link" className="rounded-full border border-stone2 px-4 py-2 text-xs font-medium text-smoke transition-colors hover:border-gold hover:text-gold">
              View Site
            </Link>
            <button data-testid="admin-logout-button" onClick={logout} className="inline-flex items-center gap-2 rounded-full bg-night px-4 py-2 text-xs font-semibold text-cream transition-transform hover:scale-[1.03]">
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-4 py-10 sm:px-8">
        <div className="flex gap-2">
          {[
            { key: "enquiries", label: "Enquiries", testId: "admin-tab-enquiries" },
            { key: "products", label: "Products", testId: "admin-tab-products" },
          ].map((t) => (
            <button
              key={t.key}
              data-testid={t.testId}
              onClick={() => setTab(t.key)}
              className={`rounded-full px-6 py-2.5 text-sm font-medium transition-all duration-300 ${
                tab === t.key ? "bg-botanical text-cream shadow-lg shadow-botanical/25" : "border border-stone2 bg-white text-smoke hover:border-gold hover:text-gold"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="mt-8">{tab === "enquiries" ? <EnquiriesPanel /> : <ProductsPanel />}</div>
      </main>
    </div>
  );
}
